import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The field a listing lays its product cards in.
 *
 *   <ProductsGrid variant={ProductsGridVariant.LISTING}>
 *     {products.map((product) => (
 *       <ProductsGridItem key={product.code}>
 *         <ProductCard … />
 *       </ProductsGridItem>
 *     ))}
 *   </ProductsGrid>
 *
 * A listing with nothing to show composes `../emptyState` in this section's
 * place rather than rendering an empty field.
 *
 * **The field only, never a card.** What stands in each cell is composed in —
 * `./productCard` while the products are here, its skeleton while they are on
 * their way, a promo banner across two cells, a contact tile in the last one —
 * and which of those is a tenant's read of its own request state. How many
 * cells there are, which page they come from and what a click on one records
 * are the tenant's too.
 *
 * **Two looks, and both are deliberate.** A category listing fills its measure
 * with as many tracks as fit at the card minimum every tenant theme declares,
 * ruled by a two-pixel gap. A recommendations block under a product steps
 * instead — one column, two from `tab`, and from `lap` as many as fit at a
 * wider minimum — ruled by a hairline. The difference is the appearance axis;
 * a column cap a page wants on top of either is that page's `className`.
 *
 * Server-first: every part is a plain component.
 */

/** The field's two looks. The clip and the rounding are shared; the tracks and the rule are not. */
export const productsGridVariants = cva('grid overflow-hidden rounded-xl', {
  variants: {
    variant: {
      /** As many tracks as fit at the theme's card minimum, ruled by a two-pixel gap. */
      listing:
        'grid-cols-[repeat(auto-fill,minmax(var(--container-prodcard-minwidth),1fr))] gap-0.5',
      /** One column, two from `tab`, and from `lap` as many as fit at 328px — ruled by a hairline. */
      recommendation:
        'tab:grid-cols-2 lap:grid-cols-[repeat(auto-fill,minmax(328px,1fr))] grid-cols-1 gap-px',
    },
  },
});

type ProductsGridVariantName = NonNullable<VariantProps<typeof productsGridVariants>['variant']>;

export type ProductsGridVariant = ProductsGridVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductsGridVariant = {
  /** A category, brand, search or content-block listing. */
  LISTING: 'listing',
  /** The accessories and related products under a product. */
  RECOMMENDATION: 'recommendation',
} as const satisfies Record<string, ProductsGridVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductsGridVariantProps = { readonly variant: ProductsGridVariant };

export type ProductsGridProps = ComponentProps<'ul'> & ProductsGridVariantProps;

/** The field of products. */
export const ProductsGrid = ({ className, variant, ...props }: ProductsGridProps) => (
  <ul
    data-slot="products-grid"
    className={clsxm(productsGridVariants({ variant }), className)}
    {...props}
  />
);

export type ProductsGridItemProps = ComponentProps<'li'>;

/**
 * One product's place in the field.
 *
 * It paints nothing, so it declares no recipe: the tracks are the field's and
 * the look is the card's. A cell that spans two tracks — a promo banner — says
 * so through its own `className`, because how wide a banner runs is a
 * decision the listing makes from how many tracks it measured.
 */
export const ProductsGridItem = ({ className, ...props }: ProductsGridItemProps) => (
  <li data-slot="products-grid-item" className={optionalClassName(clsxm(className))} {...props} />
);
